import { supabase, applyOrgFilter } from '../supabaseClient'
import { buildFilasInscripcion } from '../inscripcionRows'
import { buildParticipacionByJugador } from '../participacion'
import { normalizeSportValue } from '../../constants/sports'
import { orgExigeConsentimiento, jugadoresSinConsentimiento } from './consent'

// Jugador_ids inscritos en una temporada (estatus != 'baja'), opcionalmente
// acotado a los que HOY son miembros vivos de un grupo. Fuente para el cobro por
// inscritos del ciclo.
//
// El grupo se resuelve EN VIVO contra jugadores_grupos; la inscripcion ya no
// guarda grupo_id. Ese campo era una copia de un dato que vive en otra tabla y
// cambia por su cuenta: nacia desde grupos que el jugador ya habia dejado (el
// query de Inscripciones no filtraba activo/deleted) y nadie lo actualizaba al
// mover de grupo, asi que el cobro por grupo se saltaba jugadores en silencio.
// Misma forma que el cache jugadores_meta.deportes retirado en julio.
export const getJugadoresInscritos = async (temporadaId, { grupoId } = {}) => {
  let query = supabase
    .from('temporada_inscripciones')
    .select('jugador_id')
    .eq('temporada_id', temporadaId)
  query = query.neq('estatus', 'baja')
  query = applyOrgFilter(query)
  const { data, error } = await query
  if (error) throw error
  const inscritos = [...new Set((data || []).map((r) => r.jugador_id))]
  if (!grupoId || !inscritos.length) return inscritos
  return intersectarConGrupo(inscritos, grupoId)
}

// Los del grupo que ademas estan en la lista dada. Acotado por `.in(jugadorIds)`,
// que ya viene filtrada por org, asi que el aislamiento se mantiene sin depender
// de un org_id en jugadores_grupos (no lo tiene).
const intersectarConGrupo = async (jugadorIds, grupoId) => {
  const { data, error } = await supabase
    .from('jugadores_grupos')
    .select('jugador_id')
    .eq('grupo_id', grupoId)
    .eq('activo', true)
    .or('deleted.is.null,deleted.eq.false')
    .in('jugador_id', jugadorIds)
  if (error) throw error
  const enGrupo = new Set((data || []).map((m) => m.jugador_id))
  return jugadorIds.filter((id) => enGrupo.has(id))
}

// `conGrupos` trae ademas los grupos VIVOS del jugador (jugadores_grupos), para
// quien necesite mostrarlos o filtrar por ellos. No se pide siempre porque pesa
// y la mayoria de los llamadores solo quiere estatus.
const SELECT_BASE = '*, jugadores(id, nombre_completo, numero)'
const SELECT_CON_GRUPOS =
  '*, jugadores(id, nombre_completo, numero, jugadores_grupos!left(grupo_id, activo, deleted, grupos(id, nombre_del_grupo, deporte)))'

export const getInscripciones = async ({ temporadaId, deporteId, estatus, conGrupos = false } = {}) => {
  let query = supabase
    .from('temporada_inscripciones')
    .select(conGrupos ? SELECT_CON_GRUPOS : SELECT_BASE)
  if (temporadaId) query = query.eq('temporada_id', temporadaId)
  if (deporteId) query = query.eq('deporte_id', deporteId)
  if (estatus) query = query.eq('estatus', estatus)
  query = applyOrgFilter(query)
  const { data, error } = await query
  return { data, error }
}

// Grupos vivos del jugador de una fila de getInscripciones({ conGrupos: true }),
// acotados al deporte de la temporada. Es el reemplazo del viejo `grupos` que
// colgaba del grupo_id congelado en la inscripcion.
export const gruposVivosDeInscripcion = (insc, deporteId) => {
  const deporte = deporteId ? normalizeSportValue(deporteId) : null
  return (insc?.jugadores?.jugadores_grupos || [])
    .filter((jg) => jg.activo && !jg.deleted && jg.grupos)
    .filter((jg) => !deporte || normalizeSportValue(jg.grupos.deporte) === deporte)
    .map((jg) => ({ id: jg.grupos.id, nombre: jg.grupos.nombre_del_grupo }))
}

export const getTemporadasDeJugador = async (jugadorId) => {
  let query = supabase
    .from('temporada_inscripciones')
    .select('*, temporadas(id, nombre, deporte_id, estado)')
    .eq('jugador_id', jugadorId)
  query = applyOrgFilter(query)
  const { data, error } = await query
  return { data, error }
}

// Participacion de TODOS los jugadores de la org (spec §4.7), en una sola ida:
// getTemporadasDeJugador es por jugador y en una tabla serian N consultas.
// excluirTemporadaId = la que se esta gestionando (su estado ya lo pinta la fila).
export const getParticipacionByJugador = async ({ excluirTemporadaId = null } = {}) => {
  try {
    let tQ = supabase
      .from('temporadas')
      .select('id, deporte_id, estado, fecha_inicio')
      .is('deleted_at', null)
      .in('estado', ['abierta', 'cerrada'])
    tQ = applyOrgFilter(tQ)

    let iQ = supabase
      .from('temporada_inscripciones')
      .select('jugador_id, temporada_id, estatus')
      .neq('estatus', 'baja')
    iQ = applyOrgFilter(iQ)

    const [{ data: temporadas, error: tErr }, { data: inscripciones, error: iErr }] =
      await Promise.all([tQ, iQ])
    if (tErr) throw tErr
    if (iErr) throw iErr

    return {
      data: buildParticipacionByJugador({
        inscripciones: inscripciones || [],
        temporadas: temporadas || [],
        excluirTemporadaId,
      }),
      error: null,
    }
  } catch (error) {
    return { data: null, error }
  }
}

const ESTATUS_VALIDOS = ['pendiente', 'activo', 'baja']

// Activar una inscripcion ES activar al jugador en esa temporada, asi que exige
// lo mismo: si el club pide consentimiento, sin firma no se activa. La regla vive
// AQUI y no solo en el boton de la pantalla -- un requisito legal no puede
// depender de que cada vista se acuerde de revisarlo.
const bloqueoPorConsentimiento = async (ids) => {
  const { data: inscs, error } = await supabase
    .from('temporada_inscripciones')
    .select('id, jugador_id, org_id')
    .in('id', ids)
  if (error) return { error }

  const orgIds = [...new Set((inscs || []).map((i) => i.org_id).filter(Boolean))]
  const exigencias = await Promise.all(orgIds.map((o) => orgExigeConsentimiento(o)))
  const exigen = new Set(orgIds.filter((_, i) => exigencias[i]?.data))
  const relevantes = (inscs || []).filter((i) => exigen.has(i.org_id))
  if (!relevantes.length) return { error: null }

  const { data: sin, error: sErr } = await jugadoresSinConsentimiento(relevantes.map((i) => i.jugador_id))
  if (sErr) return { error: sErr }
  const faltan = new Set(sin || [])
  const n = relevantes.filter((i) => faltan.has(i.jugador_id)).length
  if (!n) return { error: null }

  const e = new Error(n === 1
    ? 'Falta el consentimiento del tutor para activar a este jugador.'
    : `Faltan ${n} consentimientos: esos jugadores no se pueden activar hasta que el tutor firme.`)
  e.code = 'CONSENT_REQUIRED'
  return { error: e }
}

export const setEstatusInscripcion = async (id, estatus) => {
  if (!ESTATUS_VALIDOS.includes(estatus)) {
    return { data: null, error: new Error(`Estatus inválido: ${estatus}`) }
  }
  if (estatus === 'activo') {
    const { error: gateErr } = await bloqueoPorConsentimiento([id])
    if (gateErr) return { data: null, error: gateErr }
  }
  let query = supabase
    .from('temporada_inscripciones')
    .update({ estatus, updated_at: new Date().toISOString() })
    .eq('id', id)
  query = applyOrgFilter(query)
  const { data, error } = await query.select().single()
  return { data, error }
}

// Inscribe un jugador en una temporada, en estatus 'pendiente'. Idempotente por
// UNIQUE(jugador_id, temporada_id). `temporada` = { id, org_id, deporte_id }.
// No guarda grupo: el grupo es "donde entrena" y vive en jugadores_grupos; quien
// lo necesite lo resuelve en vivo (ver getJugadoresInscritos).
export const inscribirJugador = async ({ jugadorId, temporada }) => {
  const { data, error } = await supabase
    .from('temporada_inscripciones')
    .upsert(
      {
        org_id: temporada.org_id,
        jugador_id: jugadorId,
        temporada_id: temporada.id,
        deporte_id: temporada.deporte_id,
        estatus: 'pendiente',
      },
      { onConflict: 'jugador_id,temporada_id', ignoreDuplicates: true },
    )
    .select()
  return { data, error }
}

// Inscribe VARIOS jugadores de una sola ida (una consulta, no N). `jugadores` =
// [{ jugadorId }] o [jugadorId]. Sin grupo: ver inscribirJugador.
export const inscribirJugadores = async ({ jugadores = [], temporada }) => {
  if (!jugadores.length) return { data: { inscritos: 0 }, error: null }
  const rows = jugadores.map((j) => ({
    org_id: temporada.org_id,
    jugador_id: typeof j === 'string' ? j : j.jugadorId,
    temporada_id: temporada.id,
    deporte_id: temporada.deporte_id,
    estatus: 'pendiente',
  }))
  const { error } = await supabase
    .from('temporada_inscripciones')
    .upsert(rows, { onConflict: 'jugador_id,temporada_id', ignoreDuplicates: true })
  if (error) return { data: null, error }
  return { data: { inscritos: rows.length }, error: null }
}

// Cambia el estatus de VARIAS inscripciones en una sola consulta.
export const setEstatusInscripciones = async (ids = [], estatus) => {
  if (!ESTATUS_VALIDOS.includes(estatus)) {
    return { data: null, error: new Error(`Estatus inválido: ${estatus}`) }
  }
  if (!ids.length) return { data: { actualizadas: 0 }, error: null }
  if (estatus === 'activo') {
    const { error: gateErr } = await bloqueoPorConsentimiento(ids)
    if (gateErr) return { data: null, error: gateErr }
  }
  let query = supabase
    .from('temporada_inscripciones')
    .update({ estatus, updated_at: new Date().toISOString() })
    .in('id', ids)
  query = applyOrgFilter(query)
  const { error } = await query
  if (error) return { data: null, error }
  return { data: { actualizadas: ids.length }, error: null }
}

// Estado de participacion de los jugadores de UN grupo (F2): que le falta a cada
// quien, visible donde el coach trabaja. Es la contraparte del candado suave: el
// candado exige la inscripcion; esto vigila el papeleo SIN bloquear a nadie.
//
// Devuelve { [jugadorId]: { estado, adeudo } } donde estado es el mismo derivado
// de la pantalla de Inscripciones (pendiente|activo|revision|baja|no_inscrito) y
// adeudo = tiene al menos un cargo VENCIDO. Un cargo 'pendiente' aun no vence:
// llamarle adeudo seria injusto.
//
// Reusa getFilasInscripcion (motor de reglas ya probado) en vez de duplicarlo.
export const getEstadoJugadoresGrupo = async (grupoId) => {
  try {
    const { data: grupo, error: gErr } = await supabase
      .from('grupos').select('deporte, org_id').eq('id', grupoId).single()
    if (gErr) throw gErr

    const { data: abiertas, error: tErr } = await supabase
      .from('temporadas')
      .select('id, org_id, deporte_id, nombre, estado')
      .eq('org_id', grupo.org_id)
      .eq('deporte_id', grupo.deporte)
      .eq('estado', 'abierta')
      .is('deleted_at', null)
    if (tErr) throw tErr

    // Sin temporada abierta (o con dos, ambiguo) no hay contra que comparar.
    const temporada = (abiertas || []).length === 1 ? abiertas[0] : null
    if (!temporada) return { data: { temporada: null, porJugador: {} }, error: null }

    const [{ data: filas }, { data: cargos }] = await Promise.all([
      getFilasInscripcion(temporada),
      applyOrgFilter(
        supabase
          .from('pagos_cargos')
          .select('jugador_id')
          .eq('temporada_id', temporada.id)
          .eq('estatus', 'vencido'),
      ),
    ])

    const conAdeudo = new Set((cargos || []).map((c) => c.jugador_id))
    const porJugador = {}
    ;(filas || []).forEach((f) => {
      porJugador[f.jugadorId] = {
        estado: f.estadoMostrado,
        adeudo: conAdeudo.has(f.jugadorId),
      }
    })
    return { data: { temporada, porJugador }, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

// Arma las filas de la pantalla de Inscripciones para una temporada.
// temporada = { id, org_id, deporte_id }.
export const getFilasInscripcion = async (temporada) => {
  try {
    let jQ = supabase
      .from('jugadores')
      .select('id, nombre_completo, numero, curp, fecha_nacimiento, bajo_tutela_legal, jugadores_grupos!left(grupo_id, activo, deleted, grupos(id, nombre_del_grupo, deporte))')
      .or('deleted.is.null,deleted.eq.false')
      .order('nombre_completo')
    jQ = applyOrgFilter(jQ)

    const [{ data: jugadores }, { data: inscripciones }, { data: exige }] = await Promise.all([
      jQ,
      getInscripciones({ temporadaId: temporada.id }),
      orgExigeConsentimiento(temporada.org_id),
    ])

    let cQ = supabase
      .from('consentimientos')
      .select('*')
      .eq('estado', 'firmado')
      .eq('trata_datos', true)
      .order('fecha_hora', { ascending: false })
    cQ = applyOrgFilter(cQ)
    const { data: consents } = await cQ

    const inscripcionesByJugador = {}
    ;(inscripciones || []).forEach((i) => {
      inscripcionesByJugador[i.jugador_id] = { id: i.id, estatus: i.estatus }
    })
    const consentByJugador = {}
    ;(consents || []).forEach((c) => {
      if (!consentByJugador[c.jugador_id]) consentByJugador[c.jugador_id] = c
    })

    const filas = buildFilasInscripcion({
      jugadores: jugadores || [],
      inscripcionesByJugador,
      consentByJugador,
      exigirConsentimiento: !!exige,
      deporteId: temporada.deporte_id,
    })
    return { data: filas, error: null }
  } catch (error) {
    return { data: null, error }
  }
}
